import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Button,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Search, Security, AdminPanelSettings, ManageAccounts } from '@mui/icons-material';
import ClaimsManager from '../User/ClaimsManager';
import RolesManager from '../User/RolesManager';

function UserPermissionIndex() {
  useSessionValidation();

  const history = useHistory();
  const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();
  const { canReadAdministration, canEditAdministration } = usePermissions();

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Selected user for permission management
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermData, setUserPermData] = useState({ Claim: [], Roles: [] });
  const [loadingPerms, setLoadingPerms] = useState(false);

  // Dialog state
  const [claimsDialogOpen, setClaimsDialogOpen] = useState(false);
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [isSavingClaims, setIsSavingClaims] = useState(false);
  const [isSavingRoles, setIsSavingRoles] = useState(false);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    fetchData('UserApi', `/${companyId}/${branchId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setUsers(response.response_data || []);
        }
      })
      .catch(() => setError('Failed to fetch users.'))
      .finally(() => setLoading(false));
  };

  const loadUserPermissions = async (user) => {
    setSelectedUser(user);
    setLoadingPerms(true);
    try {
      const response = await fetchData('UserApi', `/UserEdit/${user.Id}`, token);
      if (response.responseCode === '1111') {
        const { User } = response.response_data;
        setUserPermData({
          ...User,
          Claim: response.response_data.Claims?.map((c) => ({ ...c })) || [],
          Roles: response.response_data.Roles?.map((r) => ({ ...r })) || [],
        });
      }
    } catch {
      setError('Failed to load user permissions.');
    } finally {
      setLoadingPerms(false);
    }
  };

  const openClaimsDialog = async (user) => {
    await loadUserPermissions(user);
    setClaimsDialogOpen(true);
  };

  const openRolesDialog = async (user) => {
    await loadUserPermissions(user);
    setRolesDialogOpen(true);
  };

  const saveClaims = async (updatedClaims) => {
    try {
      setIsSavingClaims(true);
      const payload = { ...userPermData, Claim: updatedClaims };
      const response = await updateData(
        'UserApi',
        `SaveUserClaims/UpdateClaim/tmp/${selectedUser.Id}`,
        payload,
        token
      );
      if (response.responseCode === '1111') {
        setSuccess(`Claims updated successfully for ${selectedUser.FirstName} ${selectedUser.LastName}.`);
        setUserPermData((prev) => ({ ...prev, Claim: updatedClaims }));
      } else {
        setError('Failed to save claims.');
      }
      setClaimsDialogOpen(false);
    } catch {
      setError('Error saving claims.');
    } finally {
      setIsSavingClaims(false);
    }
  };

  const saveRoles = async (updatedRoles) => {
    try {
      setIsSavingRoles(true);
      const payload = { ...userPermData, Roles: updatedRoles };
      const response = await updateData(
        'UserApi',
        `SaveUserRoles/tmp/${selectedUser.Id}`,
        payload,
        token
      );
      if (response.responseCode === '1111') {
        setSuccess(`Role updated successfully for ${selectedUser.FirstName} ${selectedUser.LastName}.`);
        setUserPermData((prev) => ({ ...prev, Roles: updatedRoles }));
      } else {
        setError('Failed to save role.');
      }
      setRolesDialogOpen(false);
    } catch {
      setError('Error saving role.');
    } finally {
      setIsSavingRoles(false);
    }
  };

  const filteredUsers = searchTerm
    ? users.filter(
        (u) =>
          u.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.LastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.UserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.Email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;

  // Guard: no read access
  if (!canReadAdministration) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Alert severity="error">You don't have permission to access User Permissions.</Alert>
      </div>
    );
  }

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '12px',
          color: 'black',
          fontFamily: 'Times New Roman',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ManageAccounts sx={{ color: '#2C3E6E', fontSize: 22 }} />
            <h4
              style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Roman',
                textTransform: 'uppercase',
                color: 'black',
              }}
            >
              User Permissions
            </h4>
          </Box>
          <Typography sx={{ fontSize: '12px', color: '#718096', fontStyle: 'italic' }}>
            Manage user claims &amp; roles
          </Typography>
        </div>

        {/* Search */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, username, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#0f3460', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
                '&:hover fieldset': { borderColor: '#0f3460' },
                '&.Mui-focused fieldset': { borderColor: '#0f3460' },
              },
            }}
          />
        </div>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#2C3E6E' }} />
          </Box>
        ) : (
          <div style={{ marginBottom: '10px' }}>
            <TableContainer
              component={Paper}
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow
                    style={{
                      background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                      height: '27px',
                    }}
                  >
                    {['#', 'Name', 'Username', 'Contact', 'Email', 'Status', 'Claims', 'Role'].map(
                      (col) => (
                        <TableCell
                          key={col}
                          style={{
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '11px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '4px 12px',
                            height: '27px',
                            textAlign: ['Claims', 'Role'].includes(col) ? 'center' : 'left',
                          }}
                        >
                          {col}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center', padding: '24px', fontSize: '13px', color: '#718096' }}>
                        No users found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((item, index) => (
                      <TableRow
                        key={item.Id}
                        style={{
                          background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <TableCell style={{ fontSize: '12px', padding: '7px 12px', color: '#2d3748' }}>
                          {index + 1}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '7px 12px', color: '#2d3748', fontWeight: 500 }}>
                          {item.FirstName} {item.LastName}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '7px 12px', color: '#4a5568' }}>
                          {item.UserName || '-'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '7px 12px', color: '#4a5568' }}>
                          {item.ContactNumber || '-'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '7px 12px', color: '#4a5568' }}>
                          {item.Email || '-'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '7px 12px' }}>
                          <Chip
                            label={item.IsActive ? 'Active' : 'Inactive'}
                            color={item.IsActive ? 'success' : 'default'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: '20px' }}
                          />
                        </TableCell>

                        {/* Claims button */}
                        <TableCell style={{ padding: '7px 12px', textAlign: 'center' }}>
                          {canEditAdministration ? (
                            <Tooltip title="Manage user claims / permissions" arrow>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Security sx={{ fontSize: '13px' }} />}
                                onClick={() => openClaimsDialog(item)}
                                sx={{
                                  borderColor: '#2C3E6E',
                                  color: '#2C3E6E',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  padding: '2px 8px',
                                  minHeight: '24px',
                                  textTransform: 'none',
                                  borderRadius: '6px',
                                  '&:hover': { background: 'rgba(44,62,110,0.06)' },
                                }}
                              >
                                Claims
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '11px', color: '#aaa' }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Role button */}
                        <TableCell style={{ padding: '7px 12px', textAlign: 'center' }}>
                          {canEditAdministration ? (
                            <Tooltip title="Manage user role" arrow>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AdminPanelSettings sx={{ fontSize: '13px' }} />}
                                onClick={() => openRolesDialog(item)}
                                sx={{
                                  borderColor: '#11998e',
                                  color: '#11998e',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  padding: '2px 8px',
                                  minHeight: '24px',
                                  textTransform: 'none',
                                  borderRadius: '6px',
                                  '&:hover': { background: 'rgba(17,153,142,0.06)' },
                                }}
                              >
                                Role
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '11px', color: '#aaa' }}>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </div>

      {/* Loading overlay while fetching user permissions */}
      {loadingPerms && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
          }}
        >
          <Box
            sx={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px 32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            }}
          >
            <CircularProgress size={32} sx={{ color: '#2C3E6E' }} />
            <Typography sx={{ fontSize: '13px', color: '#4a5568', fontWeight: 500 }}>
              Loading permissions...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Claims Dialog */}
      <Dialog
        open={claimsDialogOpen}
        onClose={() => setClaimsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: '16px', maxHeight: '90vh' } }}
      >
        {selectedUser && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0, borderBottom: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: '11px', color: '#718096', fontWeight: 500, pb: 1 }}>
              User: <strong style={{ color: '#2C3E6E' }}>{selectedUser.FirstName} {selectedUser.LastName}</strong>
              &nbsp;|&nbsp;Username: <strong style={{ color: '#2C3E6E' }}>{selectedUser.UserName}</strong>
            </Typography>
          </Box>
        )}
        <ClaimsManager
          claims={userPermData.Claim}
          onSave={saveClaims}
          onCancel={() => setClaimsDialogOpen(false)}
          isSaving={isSavingClaims}
        />
      </Dialog>

      {/* Roles Dialog */}
      <Dialog
        open={rolesDialogOpen}
        onClose={() => setRolesDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: '16px', maxHeight: '90vh' } }}
      >
        {selectedUser && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0, borderBottom: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: '11px', color: '#718096', fontWeight: 500, pb: 1 }}>
              User: <strong style={{ color: '#2C3E6E' }}>{selectedUser.FirstName} {selectedUser.LastName}</strong>
              &nbsp;|&nbsp;Username: <strong style={{ color: '#2C3E6E' }}>{selectedUser.UserName}</strong>
            </Typography>
          </Box>
        )}
        <RolesManager
          roles={userPermData.Roles}
          onSave={saveRoles}
          onCancel={() => setRolesDialogOpen(false)}
          isSaving={isSavingRoles}
        />
      </Dialog>

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={5000}
        onClose={() => { setError(''); setSuccess(''); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setError(''); setSuccess(''); }}
          severity={error ? 'error' : 'success'}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default UserPermissionIndex;
